var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '@/styles/ChatPage.css';
import Header from '@/components/Header';
import Sidebar from '@/components/Sidebar';
import ChatWindow from '@/components/ChatWindow';
import BackgroundChat from '@/components/BackgroundChat';
import { useWebSocketContext } from '@/contexts/WebSocketContext';
import { useAuth } from '@/AuthContext';
import { startNewChat, selectOldChat, toggleSidebar, scrollToBottom, copyToClipboard, handleThumbsUp, handleThumbsDown, handleSubmitComment, clearOldConversations, createMarkup, handleWebSocketError, handleWebSocketClose, sendEmailToSupport, handleReceiveMessageWrapper, handleSendMessageWrapper, updateUserRole, checkUserExists, mapUserRole } from '@/utils/index';
const loadConversationsFromLocalStorage = () => {
    const storedConversations = localStorage.getItem('conversations');
    return storedConversations ? JSON.parse(storedConversations) : [];
};
const ChatPage = () => {
    const { userInfo, setUserInfo, showPopup, setShowPopup, isAuthenticated } = useAuth(); // Get userInfo, setUserInfo, fetchUserInfo, and setShowPopup from context
    const [inputMessage, setInputMessage] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [sources, setSources] = useState({});
    const [isTypingComplete, setIsTypingComplete] = useState(false);
    const [showOldChats, setShowOldChats] = useState(false);
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [selectedMessageId, setSelectedMessageId] = useState(null);
    const [isCopied, setIsCopied] = useState(false);
    const [conversations, setConversations] = useState(loadConversationsFromLocalStorage());
    const [messages, setMessages] = useState([]);
    const [feedback, setFeedback] = useState({});
    const [conversationID, setConversationID] = useState('');
    const [userVerificationInfo, setUserVerificationInfo] = useState({ email: '', jobTitle: '' });
    const messagesContainerRef = useRef(null);
    const [selectedConversationId, setSelectedConversationId] = useState(null);
    const handleStartNewChat = () => {
        startNewChat(setMessages, // 1) setMessages
        setFeedback, // 2) setFeedback
        // 2) setFeedback
        newConvId => {
            setConversationID(newConvId);
            setSelectedConversationId(newConvId);
        }, setConversationID, // 4) setConversationID
        ws, // 5) ws
        conversations, // 6) conversations
        setConversations, // 7) setConversations
        userInfo === null || userInfo === void 0 ? void 0 : userInfo.id // 8) userOID (optional)
        );
    };
    /** 2) Select an existing chat and highlight it */
    const handleSelectOldChat = (conversationID) => {
        // This calls your utility function that sets messages, etc.
        selectOldChat(conversationID, conversations, setMessages, setIsSidebarOpen, setConversationID, setFeedback);
        // Then highlight that chat in the menu
        setSelectedConversationId(conversationID);
        // Possibly also close the old-chats panel if you like:
        // setShowOldChats(false);
    };
    const ws = useWebSocketContext();
    const majorRoles = [
        'PLANNER',
        'PATTERN MAKER',
        'BUSINESS CONTROLLER',
        'MANAGER',
        'PRODUCT DEVELOPER',
        'PRINT DESIGNER',
        'PRODUCT DESIGNER',
        'PRODUCT MANAGER',
        'BUYER',
        'OTHER'
    ];
    const userMetadata = useMemo(() => {
        return userInfo
            ? {
                userJobTitle: userInfo.jobTitle || '',
                userTeam: userInfo.team || '',
                userOID: userInfo.id || ''
            }
            : null;
    }, [userInfo]);
    // Effect to check if the user exists
    useEffect(() => {
        const userExists = () => __awaiter(void 0, void 0, void 0, function* () {
            if (isAuthenticated && (userInfo === null || userInfo === void 0 ? void 0 : userInfo.email)) {
                try {
                    const { exists, role } = yield checkUserExists(userInfo.email);
                    if (exists) {
                        // User exists, update userInfo with the role from backend
                        setUserInfo(prev => (Object.assign(Object.assign({}, prev), { jobTitle: role || prev.jobTitle })));
                    }
                    else {
                        // User does not exist, prepopulate userVerificationInfo
                        setUserVerificationInfo({
                            email: userInfo.email || '',
                            jobTitle: mapUserRole(userInfo.jobTitle) || ''
                        });
                        setShowPopup(true); // Show the popup if the user does not exist
                    }
                }
                catch (error) {
                    console.error('Error checking user existence:', error);
                }
            }
        });
        userExists();
    }, [isAuthenticated, setShowPopup, setUserInfo, userInfo.email, userInfo.jobTitle]); // Only include necessary dependencies
    // Effect to handle showing the popup
    useEffect(() => {
        if (showPopup) {
            // Logic to handle showing the popup can go here if needed
        }
    }, [showPopup]); // Dependency on showPopup
    const handleVerifyUser = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const { email, jobTitle } = userVerificationInfo; // Get email and jobTitle from state
            const uid = userInfo === null || userInfo === void 0 ? void 0 : userInfo.id; // Use the user ID from context
            yield updateUserRole(email, jobTitle, uid, setUserInfo); // Update user role in the backend
            setShowPopup(false); // Close the popup after verification
        }
        catch (error) {
            console.error('Error verifying user:', error);
        }
    });
    const handleWebSocketMessageCallback = useCallback((event) => {
        if (userMetadata) {
            handleReceiveMessageWrapper(event, setFeedback, setIsBotTyping, setSources, setSelectedMessageId, setIsTypingComplete, setMessages, scrollToBottom, messagesContainerRef, setConversations, messages, conversationID, userMetadata, conversations);
        }
    }, [messages, conversationID, userMetadata, conversations]);
    useEffect(() => {
        if (ws) {
            ws.onmessage = handleWebSocketMessageCallback;
            ws.onerror = handleWebSocketError;
            ws.onclose = handleWebSocketClose;
        }
    }, [ws, handleWebSocketMessageCallback]);
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const storedConversations = loadConversationsFromLocalStorage();
        setConversations(storedConversations);
        if (storedConversations.length > 0) {
            const latestConversation = storedConversations[storedConversations.length - 1];
            if (latestConversation.messages && latestConversation.messages.length > 0) {
                const latestMessageTimestamp = new Date(latestConversation.messages[latestConversation.messages.length - 1].timestamp);
                const currentTime = new Date();
                const fiveMinutesAgo = new Date(currentTime.getTime() - 5 * 60 * 1000);
                if (latestMessageTimestamp >= fiveMinutesAgo) {
                    setMessages(latestConversation.messages);
                    setConversationID(latestConversation.conversationID);
                }
                else {
                    startNewChat(setMessages, // 1) setMessages
                    setFeedback, // 2) setFeedback
                    // 2) setFeedback
                    newConvId => {
                        setConversationID(newConvId);
                        setSelectedConversationId(newConvId);
                    }, setConversationID, // 4) setConversationID
                    ws, // 5) ws
                    conversations, // 6) conversations
                    setConversations, // 7) setConversations
                    userInfo === null || userInfo === void 0 ? void 0 : userInfo.id // 8) userOID (optional)
                    );
                }
            }
            else {
                startNewChat(setMessages, // 1) setMessages
                setFeedback, // 2) setFeedback
                // 2) setFeedback
                newConvId => {
                    setConversationID(newConvId);
                    setSelectedConversationId(newConvId);
                }, setConversationID, // 4) setConversationID
                ws, // 5) ws
                conversations, // 6) conversations
                setConversations, // 7) setConversations
                userInfo === null || userInfo === void 0 ? void 0 : userInfo.id // 8) userOID (optional)
                );
            }
        }
        else {
            startNewChat(setMessages, // 1) setMessages
            setFeedback, // 2) setFeedback
            // 2) setFeedback
            newConvId => {
                setConversationID(newConvId);
                setSelectedConversationId(newConvId);
            }, setConversationID, // 4) setConversationID
            ws, // 5) ws
            conversations, // 6) conversations
            setConversations, // 7) setConversations
            userInfo === null || userInfo === void 0 ? void 0 : userInfo.id // 8) userOID (optional)
            );
        }
    }, [userInfo, ws]);
    /* eslint-enable react-hooks/exhaustive-deps */
    const handleSendMessage = (e) => {
        e.preventDefault();
        if (inputMessage.trim() !== '' && userMetadata) {
            handleSendMessageWrapper(e, inputMessage, ws, setMessages, setIsBotTyping, setInputMessage, scrollToBottom, messagesContainerRef, isBotTyping, setConversations, conversations, messages, conversationID, userMetadata);
        }
    };
    return (_jsxs("div", Object.assign({ className: "chat-page" }, { children: [_jsx(BackgroundChat, {}), _jsx(Header, { isSidebarOpen: isSidebarOpen, toggleSidebar: () => toggleSidebar(isSidebarOpen, setIsSidebarOpen) }), _jsxs("div", Object.assign({ className: "main-content" }, { children: [_jsx(Sidebar, { className: isSidebarOpen ? '' : 'sidebar-closed', selectedConversationId: selectedConversationId, setSelectedConversationId: setSelectedConversationId, isSidebarOpen: isSidebarOpen, showOldChats: showOldChats, conversations: conversations, 
                        // 1) Start a new chat via this parent-level handler:
                        startNewChat: handleStartNewChat, setShowOldChats: setShowOldChats, clearOldConversations: () => clearOldConversations(setConversations), 
                        // 2) Select an old chat via this parent-level handler:
                        selectOldChat: handleSelectOldChat, openSharePoint: () => window.open('https://hennesandmauritz.sharepoint.com/sites/BUASL', '_blank'), toggleSidebar: () => toggleSidebar(isSidebarOpen, setIsSidebarOpen), setConversations: setConversations }), _jsx(ChatWindow, { className: isSidebarOpen ? 'sidebar-open' : '', selectedConversationId: selectedConversationId, setSelectedConversationId: setSelectedConversationId, messages: messages, inputMessage: inputMessage, setInputMessage: setInputMessage, handleSendMessage: handleSendMessage, isBotTyping: isBotTyping, feedback: feedback, setIsCopied: setIsCopied, isCopied: isCopied, selectedMessageId: selectedMessageId, sources: sources, handleThumbsUp: (messageId) => handleThumbsUp(messageId, conversationID, setFeedback, setMessages), handleThumbsDown: (messageId) => handleThumbsDown(messageId, conversationID, setFeedback, setMessages), handleSubmitComment: (messageId, comment) => handleSubmitComment(messageId, comment, feedback, setFeedback, setMessages, conversationID), messagesContainerRef: messagesContainerRef, ws: ws, setConversations: setConversations, setMessages: setMessages, setFeedback: setFeedback, createMarkup: createMarkup, copyToClipboard: (messageId, content) => copyToClipboard(messageId, content, setIsCopied, setFeedback, setMessages, conversationID), sendEmailToSupport: (messageID, userQuestion, chatbotResponse) => sendEmailToSupport(messageID, userQuestion, chatbotResponse, setFeedback, setMessages, conversationID), isTypingComplete: isTypingComplete, 
                        // Start new chat from the chat window if needed:
                        startNewChat: handleStartNewChat, conversationId: conversationID })] })), showPopup && (_jsx("div", Object.assign({ className: "popup-overlay" }, { children: _jsxs("div", Object.assign({ className: "popup" }, { children: [_jsx("h2", { children: "Welcome!" }), _jsx("p", { children: "Since it is your first time here, please verify that we have your information correct." }), _jsx("input", { type: "email", placeholder: "Email", value: userVerificationInfo.email, onChange: (e) => setUserVerificationInfo(Object.assign(Object.assign({}, userVerificationInfo), { email: e.target.value })) }), _jsxs("select", Object.assign({ value: userVerificationInfo.jobTitle, onChange: (e) => setUserVerificationInfo(Object.assign(Object.assign({}, userVerificationInfo), { jobTitle: e.target.value })) }, { children: [_jsx("option", Object.assign({ value: "" }, { children: "Select your role" })), majorRoles.map((role) => (_jsx("option", Object.assign({ value: role }, { children: role }), role)))] })), _jsxs("div", Object.assign({ className: "button-container" }, { children: [_jsx("button", Object.assign({ className: "button", onClick: handleVerifyUser }, { children: "SUBMIT" })), _jsx("button", Object.assign({ className: "button", onClick: () => setShowPopup(false) }, { children: "CANCEL" }))] }))] })) })))] })));
};
export default ChatPage;
